import { CheckIcon } from "@heroicons/react/24/outline"
import React, { useState, useEffect } from "react"

import shapeRoundIcon from "../../../../../assets/images/builder/v2/shape-round.svg"
import shapeSharpIcon from "../../../../../assets/images/builder/v2/shape-sharp.svg"
import shapeSoftIcon from "../../../../../assets/images/builder/v2/shape-soft.svg"
import { Button } from "../../../../components/shared/Buttons"
import Typography from "../../../../components/shared/Typography"
import ConfirmSaveModal from "../../components/shared/ConfirmSaveModal"
import { SHAPE_OPTIONS, findShapeByValues, DEFAULT_SHAPE } from "../../constants/shapeConstants"

const ShapeForm = ({ theme, onUpdate, onClose }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  // Add icons to shape options
  const shapeOptions = SHAPE_OPTIONS.map((option) => ({
    ...option,
    icon: (
      <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-gray-200">
        <img
          src={option.id === "sharp" ? shapeSharpIcon : option.id === "soft" ? shapeSoftIcon : shapeRoundIcon}
          alt={`${option.name} shape`}
          className="h-4 w-4"
        />
      </div>
    )
  }))

  // Function to find the shape ID based on current theme border radius and button style
  const findShapeId = (borderRadius, buttonStyle) => {
    const shape = findShapeByValues(borderRadius, buttonStyle)
    return shape ? shape.id : DEFAULT_SHAPE.id // Use centralized default
  }

  // Initialize selected shape from current theme
  const initialShapeId = findShapeId(theme?.borderRadius, theme?.buttonStyle)
  const [selectedShape, setSelectedShape] = useState(initialShapeId)

  // Update selected shape when theme changes
  useEffect(() => {
    const newShapeId = findShapeId(theme?.borderRadius, theme?.buttonStyle)
    setSelectedShape(newShapeId)
  }, [theme?.borderRadius, theme?.buttonStyle])

  const handleShapeSelect = (shapeId) => {
    setSelectedShape(shapeId)

    // Update the renderer immediately with temporary preview
    const selectedShapeOption = shapeOptions.find((option) => option.id === shapeId)
    const tempData = {
      borderRadius: selectedShapeOption.borderRadius,
      buttonStyle: selectedShapeOption.buttonStyle
    }
    onUpdate(tempData, { temporary: true })
  }

  const handleSave = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    const selectedShapeOption = shapeOptions.find((option) => option.id === selectedShape)
    const saveData = {
      borderRadius: selectedShapeOption.borderRadius,
      buttonStyle: selectedShapeOption.buttonStyle
    }
    onUpdate(saveData)
    onClose() // Close the form after saving
  }

  const handleCancelClick = () => {
    // Reset to the stored shape when closing without saving
    const storedShapeId = findShapeId(theme?.borderRadius, theme?.buttonStyle)
    if (selectedShape !== storedShapeId) {
      const storedShapeOption = shapeOptions.find((option) => option.id === storedShapeId)
      onUpdate(
        {
          borderRadius: storedShapeOption.borderRadius,
          buttonStyle: storedShapeOption.buttonStyle
        },
        { temporary: true }
      )
    }
    onClose()
  }

  return (
    <div className="w-full">
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-3">
          {shapeOptions.map((shape) => {
            const isSelected = selectedShape === shape.id

            return (
              <div
                key={shape.id}
                className={`rounded-lg border-2 p-4 transition-all ${
                  isSelected ? "border-teal bg-teal/5" : "border-gray-light bg-white shadow-sm hover:shadow-md"
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {shape.icon}
                    <div>
                      <Typography variant="body" className="font-medium text-gray-dark">
                        {shape.name}
                      </Typography>
                      <Typography variant="small" className="text-gray">
                        {shape.description}
                      </Typography>
                    </div>
                  </div>

                  {isSelected ? (
                    <div className="flex items-center space-x-2">
                      <CheckIcon className="h-5 w-5 text-teal" />
                      <span className="text-sm font-medium text-teal">Selected</span>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleShapeSelect(shape.id)}
                      className="flex items-center justify-center px-4 py-2 text-center">
                      Select
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 border-t border-gray-light bg-white px-6 py-4">
        <div className="flex items-center justify-end space-x-3">
          <Button type="secondary" onClick={handleCancelClick} className="px-6">
            Close
          </Button>
          <Button type="primary" onClick={handleSave} className="px-6">
            Save
          </Button>
        </div>
      </div>

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </div>
  )
}

export default ShapeForm
